import { PlayerServiceAPI } from '@API/data-services/player-service'
import { AdminTicketScan, ResponseError, ScanStatus } from '@Generated/back-office-client/src'
import {
  Button,
  Container,
  Flex,
  Heading,
  Section,
  Select,
  Table,
  Text,
  TextField,
} from '@radix-ui/themes'
import { useQuery } from '@tanstack/react-query'
import { useState, useMemo } from 'react'
import { useSelector } from 'react-redux'
import { Link, useSearchParams } from 'react-router-dom'
import { formatInTimeZone } from 'date-fns-tz'
import { subDays, isAfter } from 'date-fns'
import { RootState } from '@AppState/redux-store'
import { Routes } from '@Components/main-app/types'
import './styles.scss'

const formatEnumLabel = (value: string): string =>
  value
    .split('_')
    .map((word) => word.charAt(0) + word.slice(1).toLowerCase())
    .join(' ')

const SCAN_STATUS_OPTIONS = Object.values(ScanStatus).filter(
  (v) => v !== ScanStatus.unknown_default_open_api,
)

enum DateRange {
  ALL = 'all',
  LAST_7_DAYS = '7',
  LAST_30_DAYS = '30',
  LAST_90_DAYS = '90',
}

const ALL_STATUSES = 'all' as const

export const TicketScanSearch = () => {
  const [searchParams, setSearchParams] = useSearchParams()
  const [barcode, setBarcode] = useState(searchParams.get('barcode') ?? '')
  const [searchedBarcode, setSearchedBarcode] = useState(searchParams.get('barcode') ?? '')
  const [dateRange, setDateRange] = useState<DateRange>(DateRange.ALL)
  const [statusFilter, setStatusFilter] = useState<string>(ALL_STATUSES)

  const { activeCustomerMetadata } = useSelector((state: RootState) => state.users)
  const timezone = activeCustomerMetadata?.timezone ?? 'UTC'

  const {
    data: searchResults,
    error,
    isLoading,
  } = useQuery({
    queryKey: ['ticket-scan-search', searchedBarcode],
    queryFn: () => PlayerServiceAPI.searchTicketScansByBarcode(searchedBarcode),
    enabled: !!searchedBarcode,
  })

  const errorMessage = useMemo(() => {
    if (!error) {
      return null
    }
    if (error instanceof ResponseError) {
      return error.message
    }
    return 'An unexpected error occurred.'
  }, [error])

  const handleSearch = () => {
    setSearchedBarcode(barcode)
    setSearchParams({ barcode }, { replace: true })
  }

  const filteredScans = useMemo(() => {
    if (!searchResults) {
      return []
    }

    return searchResults.scans.filter((scan: AdminTicketScan) => {
      if (statusFilter !== ALL_STATUSES && scan.scanResponse.status !== statusFilter) {
        return false
      }

      if (dateRange !== DateRange.ALL) {
        const daysAgo = subDays(new Date(), Number(dateRange))
        if (!isAfter(new Date(scan.scanResponse.timestamp), daysAgo)) {
          return false
        }
      }

      return true
    })
  }, [searchResults, dateRange, statusFilter])

  const handleClear = () => {
    setBarcode('')
    setSearchedBarcode('')
    setDateRange(DateRange.ALL)
    setStatusFilter(ALL_STATUSES)
    setSearchParams({}, { replace: true })
  }

  return (
    <Container className="ticket-scan-search-container">
      <Section>
        <Flex direction="column" gap="3">
          <Heading as="h1" size="5">
            Ticket Scan Search
          </Heading>
          <Text>Enter search criteria to see ticket scanning activity.</Text>

          <Flex gap="3" align="end">
            <Flex direction="column" gap="1" flexGrow="1">
              <Text size="2" weight="bold">
                Barcode
              </Text>
              <TextField.Root
                placeholder="Enter barcode"
                value={barcode}
                onChange={(e) => setBarcode(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && barcode) {
                    handleSearch()
                  }
                }}
              />
            </Flex>

            <Flex direction="column" gap="1">
              <Text size="2" weight="bold">
                Date Range
              </Text>
              <Select.Root
                value={dateRange}
                disabled={!searchResults?.scans.length}
                onValueChange={(v) => setDateRange(v as DateRange)}>
                <Select.Trigger />
                <Select.Content>
                  <Select.Item value={DateRange.ALL}>All Time</Select.Item>
                  <Select.Item value={DateRange.LAST_7_DAYS}>Last 7 Days</Select.Item>
                  <Select.Item value={DateRange.LAST_30_DAYS}>Last 30 Days</Select.Item>
                  <Select.Item value={DateRange.LAST_90_DAYS}>Last 90 Days</Select.Item>
                </Select.Content>
              </Select.Root>
            </Flex>

            <Flex direction="column" gap="1">
              <Text size="2" weight="bold">
                Ticket Status
              </Text>
              <Select.Root
                value={statusFilter}
                disabled={!searchResults?.scans.length}
                onValueChange={setStatusFilter}>
                <Select.Trigger placeholder="Select Status..." />
                <Select.Content>
                  <Select.Item value={ALL_STATUSES}>All Statuses</Select.Item>
                  {SCAN_STATUS_OPTIONS.map((value) => (
                    <Select.Item key={value} value={value}>
                      {formatEnumLabel(value)}
                    </Select.Item>
                  ))}
                </Select.Content>
              </Select.Root>
            </Flex>

            <Button disabled={!barcode} onClick={handleSearch} loading={isLoading}>
              Search
            </Button>
            <Button onClick={handleClear} color="gray" variant="soft">
              Clear
            </Button>
          </Flex>

          {errorMessage ? <Text color="red">{errorMessage}</Text> : null}

          {searchResults?.scans.length === 0 && !errorMessage ? (
            <Text color="red">Barcode not found.</Text>
          ) : null}

          {searchResults &&
          searchResults.scans.length > 0 &&
          filteredScans.length === 0 &&
          !errorMessage ? (
            <Text>No results match the selected filters.</Text>
          ) : null}

          {searchResults && filteredScans.length > 0 ? (
            <>
              <Text size="2" align="right">
                Displaying results 1-{filteredScans.length}
              </Text>
              {searchResults.truncated ? (
                <Text color="orange" size="2">
                  Results limited to 100. There may be additional scans not shown.
                </Text>
              ) : null}
              <Table.Root className="results-table">
                <Table.Header>
                  <Table.Row>
                    <Table.ColumnHeaderCell>Date & Time</Table.ColumnHeaderCell>
                    <Table.ColumnHeaderCell>Barcode</Table.ColumnHeaderCell>
                    <Table.ColumnHeaderCell>Game</Table.ColumnHeaderCell>
                    <Table.ColumnHeaderCell>Ticket Status</Table.ColumnHeaderCell>
                    <Table.ColumnHeaderCell>Player ID</Table.ColumnHeaderCell>
                    <Table.ColumnHeaderCell>Device ID</Table.ColumnHeaderCell>
                  </Table.Row>
                </Table.Header>
                <Table.Body>
                  {filteredScans.map((scan: AdminTicketScan) => (
                    <Table.Row key={scan.scanResponse.scanId}>
                      <Table.RowHeaderCell>
                        {formatInTimeZone(scan.scanResponse.timestamp, timezone, 'PP')}
                        <br />
                        {formatInTimeZone(scan.scanResponse.timestamp, timezone, 'p')}
                      </Table.RowHeaderCell>
                      <Table.Cell>{searchedBarcode}</Table.Cell>
                      <Table.Cell>{formatEnumLabel(scan.scanResponse.ticketType)}</Table.Cell>
                      <Table.Cell>{scan.scanResponse.details}</Table.Cell>
                      <Table.Cell>
                        {scan.playerLookupId ? (
                          <Link
                            className="player-link"
                            to={Routes.PLAYER_DETAILS.replace(':lookupId', scan.playerLookupId)}>
                            {scan.playerLookupId}
                          </Link>
                        ) : (
                          'N/A'
                        )}
                      </Table.Cell>
                      <Table.Cell>{scan.deviceLookupId ?? 'N/A'}</Table.Cell>
                    </Table.Row>
                  ))}
                </Table.Body>
              </Table.Root>
            </>
          ) : null}
        </Flex>
      </Section>
    </Container>
  )
}
